import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useNavigate } from 'react-router';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';

import Api from '@barboxx/api';
import { PackagingFormat } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

interface Props {
    packagingFormat: PackagingFormat;
}

export const PackagingFormatDeleteCard = ({ packagingFormat }: Props) => {
    const notifications = useNotifications();
    const navigate = useNavigate();
    const [open, dialogActions] = useBool();

    const mutateDeletePackagingFormat = useMutation({
        mutationKey: ['deletePackagingFormat', packagingFormat.id],
        mutationFn: () =>
            Api.Servings.PackagingFormat.deletePackagingFormat(packagingFormat),
    });

    const onDelete = async () => {
        mutateDeletePackagingFormat
            .mutateAsync()
            .then(({ data }) => {
                notifications.show('Deleted packaging format!', {
                    severity: 'success',
                });
                navigate(-1);
            })
            .catch(e => {
                notifications.show('Error deleting packaging format!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="Delete Packaging Format" />
            <CardContent>
                <Stack gap={2} direction="row">
                    <Typography flex={1}>
                        Are you sure you want to delete this packaging format
                        and all associated products with it?
                    </Typography>

                    <Button
                        onClick={dialogActions.on}
                        color="error"
                        variant="outlined"
                    >
                        Delete
                    </Button>
                </Stack>
            </CardContent>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Delete Packaging Format
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this packaging format?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={dialogActions.off} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        onClick={onDelete}
                        color="error"
                        variant="contained"
                        loading={mutateDeletePackagingFormat.isPending}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
